/** Clinician-facing parent-message thread list + reply composer. */
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { supabase } from '../../lib/supabase/client';
import { formatShortDate } from '../../lib/effectiveslp/domain';

interface Props {
  studentId: string;
  practiceSetIds: string[];
  onClose: () => void;
  onMarkedRead?: () => void;
}

interface Thread {
  id: string;
  practice_set_id: string;
  clinician_user_id: string | null;
  parent_email_pending: string | null;
  last_message_at: string | null;
  status: string;
}

interface Message {
  id: string;
  thread_id: string;
  sender_user_id: string;
  body: string;
  sent_at: string;
  read_by_clinician_at: string | null;
}

function generateUUID(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers / non-secure contexts — never return a
  // constant UUID, or `client_message_id` would collide across replies
  // (the edge function dedupes on it) and React `key`s would clash on
  // optimistic local messages.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function ParentMessagesInbox({
  studentId,
  practiceSetIds,
  onClose,
  onMarkedRead,
}: Props): ReactElement {
  const [threads, setThreads] = useState<Thread[]>([]);
  const [messagesByThread, setMessagesByThread] = useState<
    Record<string, Message[]>
  >({});
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const practiceSetIdsKey = practiceSetIds.join(',');

  useEffect(() => {
    if (practiceSetIds.length === 0) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const { data, error: threadsErr } = await supabase
          .from('parent_message_threads')
          .select(
            'id, practice_set_id, clinician_user_id, parent_email_pending, last_message_at, status',
          )
          .in('practice_set_id', practiceSetIds)
          .order('last_message_at', { ascending: false });
        if (cancelled) return;
        if (threadsErr) {
          setError(threadsErr.message);
          setLoading(false);
          return;
        }
        const threadRows = (data ?? []) as Thread[];
        setThreads(threadRows);

        if (threadRows.length > 0) {
          const threadIds = threadRows.map((t) => t.id);
          const { data: msgData, error: msgErr } = await supabase
            .from('parent_messages')
            .select(
              'id, thread_id, sender_user_id, body, sent_at, read_by_clinician_at',
            )
            .in('thread_id', threadIds)
            .order('sent_at', { ascending: false });
          if (!cancelled && !msgErr && msgData) {
            const byThread: Record<string, Message[]> = {};
            for (const m of msgData as Message[]) {
              const arr = byThread[m.thread_id] ?? [];
              arr.push(m);
              byThread[m.thread_id] = arr;
            }
            setMessagesByThread(byThread);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId, practiceSetIdsKey, practiceSetIds]);

  // When the clinician opens a thread, mark every unread parent message in
  // that thread as read (so the badge on the Practice tab clears).
  // Fire-and-forget per message — failures don't block the UI.
  useEffect(() => {
    if (!selectedThreadId) return;
    const unread = (messagesByThread[selectedThreadId] ?? []).filter(
      (m) => !m.read_by_clinician_at,
    );
    if (unread.length === 0) return;
    let cancelled = false;
    void (async () => {
      const now = new Date().toISOString();
      for (const m of unread) {
        try {
          const { error: markErr } = await supabase.functions.invoke(
            'api-mark-parent-message-read',
            { body: { message_id: m.id } },
          );
          if (markErr) continue;
          if (cancelled) return;
          setMessagesByThread((prev) => {
            const arr = prev[selectedThreadId] ?? [];
            return {
              ...prev,
              [selectedThreadId]: arr.map((row) =>
                row.id === m.id ? { ...row, read_by_clinician_at: now } : row,
              ),
            };
          });
        } catch {
          // Swallow: a single failed mark-read shouldn't disable the UI.
        }
      }
      if (!cancelled) onMarkedRead?.();
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedThreadId, messagesByThread, onMarkedRead]);

  async function handleSend(): Promise<void> {
    if (!selectedThreadId || replyText.trim().length === 0) return;
    setSending(true);
    setError(null);
    try {
      const { error: sendErr } = await supabase.functions.invoke(
        'api-send-parent-message',
        {
          body: {
            thread_id: selectedThreadId,
            body: replyText.trim(),
            client_message_id: generateUUID(),
          },
        },
      );
      if (sendErr) {
        setError(sendErr.message);
        return;
      }
      setReplyText('');
      // Optimistically append the reply to the local list.
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const sent: Message = {
        id: generateUUID(),
        thread_id: selectedThreadId,
        sender_user_id: user?.id ?? '',
        body: replyText.trim(),
        sent_at: new Date().toISOString(),
        read_by_clinician_at: new Date().toISOString(),
      };
      setMessagesByThread((prev) => ({
        ...prev,
        [selectedThreadId]: [sent, ...(prev[selectedThreadId] ?? [])],
      }));
      setThreads((prev) =>
        prev.map((t) =>
          t.id === selectedThreadId
            ? { ...t, last_message_at: sent.sent_at }
            : t,
        ),
      );
    } finally {
      setSending(false);
    }
  }

  const selectedThread = threads.find((t) => t.id === selectedThreadId);
  const selectedMessages = selectedThreadId
    ? (messagesByThread[selectedThreadId] ?? [])
    : [];

  if (loading) {
    return (
      <section className="card">
        <button type="button" className="link-button" onClick={onClose}>
          ← Back
        </button>
        <p className="tagline">Loading messages…</p>
      </section>
    );
  }

  return (
    <section className="card stack-md">
      <button type="button" className="link-button" onClick={onClose}>
        ← Back
      </button>
      <h2>Parent messages</h2>
      {error && <p className="error">{error}</p>}
      {threads.length === 0 ? (
        <p className="tagline">
          No parent message threads yet. Send a practice link to start the
          conversation.
        </p>
      ) : selectedThread ? (
        <>
          <button
            type="button"
            className="link-button"
            onClick={() => setSelectedThreadId(null)}
          >
            ← All threads
          </button>
          <div className="card">
            <span className="caseload-row-name">
              Thread with {selectedThread.parent_email_pending ?? 'parent'}
            </span>
          </div>
          <div className="stack-md">
            {[...selectedMessages].reverse().map((m) => (
              <div
                key={m.id}
                className={`card ${m.sender_user_id === selectedThread?.clinician_user_id ? '' : 'tagline'}`}
              >
                <p>{m.body}</p>
                <span className="tagline">
                  {formatShortDate(m.sent_at)}
                </span>
              </div>
            ))}
          </div>
          <label>
            Reply
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              rows={3}
              placeholder="Write a reply to the parent…"
            />
          </label>
          <button
            type="button"
            className="snap-button"
            disabled={sending || replyText.trim().length === 0}
            onClick={() => void handleSend()}
          >
            {sending ? 'Sending…' : 'Send reply'}
          </button>
        </>
      ) : (
        <div className="stack-md">
          {threads.map((t) => {
            const latest = messagesByThread[t.id]?.[0];
            // Unread = parent-sent AND not yet read by the clinician.
            // Exclude clinician-sent rows defensively (#307): the server
            // now stamps read_by_clinician_at on clinician-sent inserts,
            // but pre-fix rows from before that change would otherwise
            // keep the "Unread" pill stuck on the clinician's own
            // replies.
            const unread = (messagesByThread[t.id] ?? []).some(
              (m) =>
                m.sender_user_id !== t.clinician_user_id &&
                !m.read_by_clinician_at,
            );
            return (
              <button
                type="button"
                key={t.id}
                className="caseload-row"
                onClick={() => setSelectedThreadId(t.id)}
              >
                <span className="caseload-row-name">
                  {t.parent_email_pending ?? 'Parent'}
                </span>
                <span className="caseload-row-meta">
                  {latest ? `${latest.body.slice(0, 60)}…` : 'No messages yet'}
                </span>
                <span className="tagline">
                  {t.last_message_at
                    ? `Last message: ${formatShortDate(t.last_message_at)}`
                    : 'No messages yet'}
                  {unread ? ' · Unread' : ''}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}

export default ParentMessagesInbox;
